import React, { useState } from "react";
import { DEMO_PATENT_UI_LIMIT } from "./accountDetailUtils";
import { partyTheme } from "./accountDetailTheme";

const listScrollBox = {
  maxHeight: "min(26vh, 220px)",
  overflowY: "auto",
  padding: "5px",
  border: `1px solid ${partyTheme.chrome.panelBorder}`,
  background: "#fafaf9",
  display: "flex",
  flexDirection: "column",
  gap: "4px",
  boxSizing: "border-box",
};

function demoPatentSlice(patents) {
  const total = patents.length;
  const shown = patents.slice(0, DEMO_PATENT_UI_LIMIT);
  const omitted = Math.max(0, total - shown.length);
  return { shown, total, omitted };
}

function DemoPatentFootnote({ visibleCount, total, omitted }) {
  if (total === 0) return null;
  return (
    <div
      style={{
        fontSize: "9px",
        lineHeight: 1.35,
        color: "#7a756c",
        marginTop: "5px",
        marginBottom: "2px",
      }}
    >
      Demo UI: showing {visibleCount.toLocaleString()} of{" "}
      {total.toLocaleString()} patent{total === 1 ? "" : "s"} in this portfolio.
      {omitted > 0 ? (
        <span>
          {" "}
          A full build would paginate or search instead of loading all rows.
        </span>
      ) : null}
    </div>
  );
}

function isPatentProcessing(patent) {
  if (patent.processing === true) return true;
  if (patent.status === "processing") return true;
  if (patent.patent_status === "processing") return true;
  return false;
}

function ProcessingPatentRow({ patentId, palette }) {
  return (
    <div
      style={{
        width: "100%",
        boxSizing: "border-box",
        background: palette.bg,
        border: `1px dashed ${palette.border}`,
        color: palette.text,
        padding: "5px 7px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "8px",
      }}
    >
      <span
        style={{
          fontFamily: "ui-monospace, monospace",
          fontSize: "11px",
          fontWeight: 600,
          letterSpacing: "0.02em",
        }}
      >
        {patentId}
      </span>
      <span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
        <span
          style={{
            width: "10px",
            height: "10px",
            borderRadius: "50%",
            border: "2px solid rgba(0,0,0,0.15)",
            borderTopColor: palette.text,
            animation: "spin 0.85s linear infinite",
          }}
        />
        <span
          style={{
            fontSize: "8px",
            textTransform: "uppercase",
            letterSpacing: "0.05em",
            color: "#6a5f52",
          }}
        >
          Processing
        </span>
      </span>
    </div>
  );
}

function PatentRow({
  patent,
  palette,
  portfolioId,
  onRemovePatentFromPortfolio,
  onRenamePatent,
  onOpenPatentReader,
}) {
  const [editing, setEditing] = useState(false);
  const [draftName, setDraftName] = useState("");
  const [saving, setSaving] = useState(false);

  const processing = isPatentProcessing(patent);
  const docLabel =
    patent.patent_id != null && String(patent.patent_id).trim() !== ""
      ? String(patent.patent_id)
      : null;

  const startEdit = () => {
    setDraftName(patent.patent_name != null ? patent.patent_name : "");
    setEditing(true);
  };

  const cancelEdit = () => {
    setEditing(false);
    setDraftName("");
  };

  const saveEdit = async () => {
    const next =
      draftName.trim() === "" ? null : draftName.trim();
    setSaving(true);
    try {
      await onRenamePatent(portfolioId, patent.patentUuid, next);
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      style={{
        width: "100%",
        boxSizing: "border-box",
        background: palette.bg,
        border: `1px solid ${palette.border}`,
        color: palette.text,
        padding: "5px 7px",
        borderRadius: 0,
        display: "flex",
        flexDirection: "column",
        gap: "4px",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "6px",
          minHeight: "18px",
        }}
      >
        <button
          type="button"
          onClick={() => onOpenPatentReader(portfolioId, patent)}
          style={{
            background: "none",
            border: "none",
            fontFamily: "ui-monospace, monospace",
            fontSize: "11px",
            fontWeight: 700,
            letterSpacing: "0.02em",
            color: palette.text,
            flex: "1 1 auto",
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            cursor: "pointer",
            textAlign: "left",
            padding: 0,
          }}
          title={docLabel || undefined}
        >
          {docLabel ?? "—"}
        </button>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "4px",
            flexShrink: 0,
          }}
        >
          {processing && (
            <span
              style={{
                fontSize: "8px",
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.05em",
                padding: "1px 4px",
                background: "rgba(0,0,0,0.06)",
                border: "1px solid rgba(0,0,0,0.1)",
                color: "#6a5f52",
                whiteSpace: "nowrap",
              }}
              title="Patent data is still being processed"
            >
              Processing
            </span>
          )}
          {!editing && (
            <button
              type="button"
              onClick={startEdit}
              style={{
                background: "none",
                border: "none",
                color: palette.mutedAction,
                cursor: "pointer",
                fontSize: "9px",
                fontWeight: 600,
                padding: "1px 3px",
                textDecoration: "underline",
                textUnderlineOffset: "2px",
              }}
            >
              Rename
            </button>
          )}
          <button
            type="button"
            onClick={() =>
              onRemovePatentFromPortfolio(portfolioId, patent.patentUuid)
            }
            title="Remove from portfolio"
            style={{
              background: "none",
              border: "none",
              color: "#999",
              cursor: "pointer",
              fontSize: "13px",
              lineHeight: 1,
              padding: "0 1px",
            }}
          >
            ×
          </button>
        </div>
      </div>

      {!editing ? (
        <div
          style={{
            fontSize: "10px",
            lineHeight: 1.3,
            color: patent.patent_name ? palette.subtext : "#999",
            fontStyle: patent.patent_name ? "normal" : "italic",
            wordBreak: "break-word",
          }}
          title={patent.patent_name || undefined}
        >
          {patent.patent_name?.trim()
            ? patent.patent_name.trim()
            : "No display name yet"}
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          <input
            type="text"
            value={draftName}
            onChange={(e) => setDraftName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                saveEdit();
              }
              if (e.key === "Escape") cancelEdit();
            }}
            placeholder="Display name (optional)"
            disabled={saving}
            autoFocus
            style={{
              width: "100%",
              boxSizing: "border-box",
              padding: "4px 6px",
              border: "1px solid #c8c4bc",
              borderRadius: 0,
              fontSize: "11px",
              lineHeight: 1.25,
              background: "#fff",
              color: "#222",
            }}
          />
          <div style={{ display: "flex", gap: "4px", justifyContent: "flex-end" }}>
            <button
              type="button"
              onClick={cancelEdit}
              disabled={saving}
              style={{
                padding: "3px 10px",
                fontSize: "10px",
                fontWeight: 600,
                border: "1px solid #ccc",
                background: "#fff",
                cursor: saving ? "not-allowed" : "pointer",
                color: "#444",
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={saveEdit}
              disabled={saving}
              style={{
                padding: "3px 10px",
                fontSize: "10px",
                fontWeight: 600,
                border: `1px solid ${palette.buttonBorder}`,
                background: palette.button,
                color: "#fff",
                cursor: saving ? "not-allowed" : "pointer",
              }}
            >
              {saving ? "…" : "Save"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function AccountPortfolioSetup({
  loading,
  defaultPortfolio,
  defaultPortfolioPatents,
  defaultProcessingPatents,
  selectedCompPortfolioId,
  competitorPortfolioName,
  competitorPortfolioPatents,
  competitorProcessingPatents,
  newMyPatentId,
  onChangeNewMyPatentId,
  newCompPatentId,
  onChangeNewCompPatentId,
  onAddPatentToPortfolio,
  onRemovePatentFromPortfolio,
  onRenamePatent,
  onOpenPatentReader,
}) {
  const yoursPalette = {
    bg: partyTheme.yours.bg,
    border: partyTheme.yours.border,
    text: partyTheme.yours.text,
    subtext: "#5c534a",
    button: partyTheme.yours.button,
    buttonBorder: partyTheme.yours.buttonBorder,
    mutedAction: partyTheme.yours.textLabel || "#5c534a",
  };

  const theirsPalette = {
    bg: partyTheme.theirs.bg,
    border: partyTheme.theirs.border,
    text: partyTheme.theirs.text,
    subtext: "#5c534a",
    button: partyTheme.yours.button,
    buttonBorder: partyTheme.yours.buttonBorder,
    mutedAction: partyTheme.theirs.textLabel || "#5c534a",
  };

  const myPatentsUi = demoPatentSlice(defaultPortfolioPatents);
  const compPatentsUi = demoPatentSlice(competitorPortfolioPatents);
  const myProcessingUi = demoPatentSlice(defaultProcessingPatents);
  const compProcessingUi = demoPatentSlice(competitorProcessingPatents);

  return (
    <aside
      style={{
        width: "300px",
        minWidth: "260px",
        maxWidth: "320px",
        background: "#fff",
        borderRight: `1px solid ${partyTheme.chrome.sidebarRule}`,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <style>{"@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }"}</style>
      <div
        style={{
          padding: "10px 12px",
          borderBottom: `1px solid ${partyTheme.chrome.neutralRule}`,
          flexShrink: 0,
        }}
      >
        <h2
          style={{
            fontSize: "14px",
            fontWeight: 600,
            marginBottom: "2px",
            color: partyTheme.chrome.heading,
            lineHeight: 1.2,
          }}
        >
          Patents
        </h2>
        <div
          style={{
            fontSize: "10px",
            color: "#5c5a54",
            lineHeight: 1.3,
          }}
        >
          Edit your default portfolio and the competitor portfolio selected in
          the bar above.
        </div>
      </div>

      <div style={{ flex: 1, overflowY: "auto", padding: "10px 10px 12px" }}>
        {loading && (
          <p style={{ fontSize: "12px", lineHeight: 1.3, color: "#666" }}>
            Loading portfolios...
          </p>
        )}

        <div style={{ marginBottom: "14px" }}>
          <div
            style={{
              fontSize: "10px",
              fontWeight: 600,
              color: "#666",
              textTransform: "uppercase",
              letterSpacing: "0.04em",
              marginBottom: "6px",
              lineHeight: 1.2,
            }}
          >
            My Portfolio
            {defaultPortfolio && (
              <span
                style={{
                  fontSize: "10px",
                  fontWeight: 500,
                  color: "#999",
                  textTransform: "none",
                  marginLeft: "6px",
                  letterSpacing: "0.01em",
                }}
              >
                ({defaultPortfolio.portfolio_name})
              </span>
            )}
          </div>

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "6px",
              marginBottom: "10px",
              minHeight: "32px",
            }}
          >
            {!defaultPortfolio ? (
              <div
                style={{
                  fontSize: "11px",
                  lineHeight: 1.3,
                  color: "#999",
                  fontStyle: "italic",
                  padding: "4px 0",
                }}
              >
                Default portfolio not loaded
              </div>
            ) : defaultPortfolioPatents.length === 0 &&
              defaultProcessingPatents.length === 0 ? (
              <div
                style={{
                  fontSize: "10px",
                  lineHeight: 1.3,
                  color: "#999",
                  fontStyle: "italic",
                  padding: "4px 0",
                }}
              >
                No patents added yet
              </div>
            ) : (
              <>
                <div style={listScrollBox}>
                  {myPatentsUi.shown.map((patent) => (
                    <PatentRow
                      key={patent.patentUuid}
                      patent={patent}
                      palette={yoursPalette}
                      portfolioId={defaultPortfolio.portfolio_id}
                      onRemovePatentFromPortfolio={onRemovePatentFromPortfolio}
                      onRenamePatent={onRenamePatent}
                      onOpenPatentReader={onOpenPatentReader}
                    />
                  ))}
                  {myProcessingUi.shown.map((patent, idx) => (
                    <ProcessingPatentRow
                      key={`${patent.patent_id}-${idx}`}
                      patentId={patent.patent_id}
                      palette={yoursPalette}
                    />
                  ))}
                </div>
                <DemoPatentFootnote
                  visibleCount={myPatentsUi.shown.length + myProcessingUi.shown.length}
                  total={myPatentsUi.total + myProcessingUi.total}
                  omitted={myPatentsUi.omitted + myProcessingUi.omitted}
                />
              </>
            )}
          </div>

          <div style={{ display: "flex", gap: "4px", marginTop: "8px" }}>
            <input
              type="text"
              placeholder="Add patent ID..."
              value={newMyPatentId}
              onChange={(e) => onChangeNewMyPatentId(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === "Enter") {
                  onAddPatentToPortfolio(
                    defaultPortfolio?.portfolio_id,
                    newMyPatentId,
                  );
                }
              }}
              style={{
                flex: 1,
                padding: "4px 8px",
                border: "1px solid #d0d0d0",
                borderRadius: 0,
                fontSize: "12px",
                lineHeight: 1.25,
                background: "#fff",
              }}
            />
            <button
              onClick={() =>
                onAddPatentToPortfolio(
                  defaultPortfolio?.portfolio_id,
                  newMyPatentId,
                )
              }
              disabled={!defaultPortfolio}
              style={{
                background: defaultPortfolio ? partyTheme.yours.button : "#ccc",
                color: "white",
                border: defaultPortfolio
                  ? `1px solid ${partyTheme.yours.buttonBorder}`
                  : "none",
                padding: "4px 10px",
                borderRadius: 0,
                cursor: defaultPortfolio ? "pointer" : "not-allowed",
                fontSize: "11px",
                fontWeight: 600,
                lineHeight: 1.2,
                textTransform: "uppercase",
              }}
            >
              Add
            </button>
          </div>
        </div>

        <div style={{ marginBottom: "12px" }}>
          <div
            style={{
              fontSize: "10px",
              fontWeight: 600,
              color: "#666",
              textTransform: "uppercase",
              letterSpacing: "0.04em",
              marginBottom: "6px",
              lineHeight: 1.25,
            }}
          >
            Competitor patents
            {competitorPortfolioName ? (
              <span
                style={{
                  fontSize: "10px",
                  fontWeight: 500,
                  color: "#999",
                  textTransform: "none",
                  marginLeft: "6px",
                  letterSpacing: "0.01em",
                }}
              >
                ({competitorPortfolioName})
              </span>
            ) : null}
          </div>

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "6px",
              marginBottom: "10px",
              minHeight: "32px",
            }}
          >
            {competitorPortfolioPatents.length === 0 &&
            competitorProcessingPatents.length === 0 ? (
              <div
                style={{
                  fontSize: "10px",
                  lineHeight: 1.3,
                  color: "#999",
                  fontStyle: "italic",
                  padding: "4px 0",
                }}
              >
                {selectedCompPortfolioId
                  ? "No patents added yet"
                  : "Select a competitor portfolio in the bar above"}
              </div>
            ) : (
              <>
                <div style={listScrollBox}>
                  {compPatentsUi.shown.map((patent) => (
                    <PatentRow
                      key={patent.patentUuid}
                      patent={patent}
                      palette={theirsPalette}
                      portfolioId={selectedCompPortfolioId}
                      onRemovePatentFromPortfolio={onRemovePatentFromPortfolio}
                      onRenamePatent={onRenamePatent}
                      onOpenPatentReader={onOpenPatentReader}
                    />
                  ))}
                  {compProcessingUi.shown.map((patent, idx) => (
                    <ProcessingPatentRow
                      key={`${patent.patent_id}-${idx}`}
                      patentId={patent.patent_id}
                      palette={theirsPalette}
                    />
                  ))}
                </div>
                <DemoPatentFootnote
                  visibleCount={compPatentsUi.shown.length + compProcessingUi.shown.length}
                  total={compPatentsUi.total + compProcessingUi.total}
                  omitted={compPatentsUi.omitted + compProcessingUi.omitted}
                />
              </>
            )}
          </div>

          <div style={{ display: "flex", gap: "4px", marginTop: "8px" }}>
            <input
              type="text"
              placeholder="Add patent ID..."
              value={newCompPatentId}
              onChange={(e) => onChangeNewCompPatentId(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === "Enter") {
                  onAddPatentToPortfolio(
                    selectedCompPortfolioId,
                    newCompPatentId,
                  );
                }
              }}
              disabled={!selectedCompPortfolioId}
              style={{
                flex: 1,
                padding: "4px 8px",
                border: "1px solid #d0d0d0",
                borderRadius: 0,
                fontSize: "12px",
                lineHeight: 1.25,
                background: "#fff",
              }}
            />
            <button
              onClick={() =>
                onAddPatentToPortfolio(
                  selectedCompPortfolioId,
                  newCompPatentId,
                )
              }
              disabled={!selectedCompPortfolioId}
              style={{
                background: selectedCompPortfolioId
                  ? partyTheme.yours.button
                  : "#ccc",
                color: "white",
                border: selectedCompPortfolioId
                  ? `1px solid ${partyTheme.yours.buttonBorder}`
                  : "none",
                padding: "4px 10px",
                borderRadius: 0,
                cursor: selectedCompPortfolioId ? "pointer" : "not-allowed",
                fontSize: "11px",
                fontWeight: 600,
                lineHeight: 1.2,
                textTransform: "uppercase",
              }}
            >
              Add
            </button>
          </div>
        </div>
      </div>
    </aside>
  );
}

export default AccountPortfolioSetup;
